/**
 * GovServiceConfigPage — `/settings/gov-service-config`.
 *
 * Live read from `GET /v1/government/services` (Phase 3b service-catalogue).
 * Per-service operator overrides (fee adjustments, SLA tweaks, active flag)
 * are persisted to the generic settings-collection store on service-audit
 * (`/v1/settings-collections/gov_service_overrides`). The UI displays the
 * merged view; rule of resolution is "operator override wins over base".
 */
import { type JSX, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { listGovernmentServices } from '../../lib/api/government';
import {
  getSettingsCollection,
  putSettingsCollection,
} from '../../lib/api/settings';
import { PageHeader } from '../shared/StaffUI';

interface Override {
  readonly code: string;
  readonly nipostFeeMinor?: number | null;
  readonly slaHours?: number | null;
  readonly active?: boolean;
}

const COLLECTION_KEY = 'gov_service_overrides' as const;

export function GovServiceConfigPage(): JSX.Element {
  const qc = useQueryClient();

  const services = useQuery({
    queryKey: ['government', 'services', 'config'],
    queryFn: () => listGovernmentServices({ activeOnly: false, pageSize: 100 }),
    retry: false,
    staleTime: 30_000,
  });

  const overridesQuery = useQuery({
    queryKey: ['settings-collection', COLLECTION_KEY],
    queryFn: () => getSettingsCollection<Override>(COLLECTION_KEY),
    retry: false,
    staleTime: 30_000,
  });

  const [overrides, setOverrides] = useState<readonly Override[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (overridesQuery.data && !dirty) {
      setOverrides(overridesQuery.data.items);
    }
  }, [overridesQuery.data, dirty]);

  const saveMut = useMutation({
    mutationFn: (next: readonly Override[]) =>
      putSettingsCollection<Override>(COLLECTION_KEY, next),
    onSuccess: async () => {
      setDirty(false);
      await qc.invalidateQueries({ queryKey: ['settings-collection', COLLECTION_KEY] });
    },
  });

  function setOverride(code: string, patch: Partial<Override>): void {
    const idx = overrides.findIndex((o) => o.code === code);
    let next: readonly Override[];
    if (idx === -1) {
      next = [...overrides, { code, ...patch }];
    } else {
      next = overrides.map((o, i) => (i === idx ? { ...o, ...patch } : o));
    }
    setOverrides(next);
    setDirty(true);
  }

  const denied =
    services.isError &&
    services.error instanceof ApiError &&
    (services.error.status === 401 || services.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Government services"
        description="Configure MDA integrations, fees and eligibility tiers."
        actions={
          dirty ? (
            <button
              type="button"
              onClick={() => saveMut.mutate(overrides)}
              disabled={saveMut.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium disabled:opacity-60"
            >
              <Save className="w-4 h-4" /> {saveMut.isPending ? 'Saving…' : 'Save overrides'}
            </button>
          ) : null
        }
      />

      {saveMut.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Save failed: {(saveMut.error as Error).message}
        </div>
      ) : null}

      {services.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
          The dev token does not carry <code className="font-mono">GovernmentService/read</code>.
        </div>
      ) : services.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Service unreachable: {(services.error as Error).message}
        </div>
      ) : (services.data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          No government services configured.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Code</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Service</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Agency</th>
                <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Fee</th>
                <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-muted">NIPOST charge</th>
                <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-muted">SLA hours</th>
                <th className="text-center px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Active</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {services.data?.items.map((s) => {
                const ov = overrides.find((o) => o.code === s.code);
                const nipostFee =
                  ov?.nipostFeeMinor !== undefined && ov.nipostFeeMinor !== null
                    ? String(ov.nipostFeeMinor)
                    : (s.nipostFee ?? '');
                const slaHours =
                  ov?.slaHours !== undefined && ov.slaHours !== null
                    ? ov.slaHours
                    : s.slaHours ?? null;
                const active = ov?.active !== undefined ? ov.active : s.active;
                return (
                  <tr key={s.code} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-xs font-mono">{s.code}</td>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{s.displayName}</td>
                    <td className="px-4 py-2 text-xs">{s.agency}</td>
                    <td className="px-4 py-2 text-xs text-right font-mono">{s.agencyFee ?? '—'}</td>
                    <td className="px-4 py-2 text-xs text-right">
                      <input
                        type="text"
                        value={nipostFee}
                        aria-label={`NIPOST fee ${s.code}`}
                        onChange={(e) =>
                          setOverride(s.code, {
                            nipostFeeMinor: e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                        className="w-24 px-2 py-1 border border-border rounded text-xs text-right font-mono"
                      />
                    </td>
                    <td className="px-4 py-2 text-xs text-right">
                      <input
                        type="number"
                        value={slaHours ?? ''}
                        aria-label={`SLA hours ${s.code}`}
                        onChange={(e) =>
                          setOverride(s.code, {
                            slaHours: e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                        className="w-20 px-2 py-1 border border-border rounded text-xs text-right"
                      />
                    </td>
                    <td className="px-4 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={active}
                        aria-label={`Active ${s.code}`}
                        onChange={(e) => setOverride(s.code, { active: e.target.checked })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
